/**
 * P&L grid density (New look only). Compact packs more months and rows on
 * screen; Comfortable keeps the original spacing. Stored per viewer in
 * localStorage and on the user doc (uiPreferences.forecastDensity) so it
 * follows them across devices. Classic ignores it entirely.
 */
import { useCallback, useEffect, useSyncExternalStore } from 'react';
// @ts-ignore - JavaScript module without types
import { useUserSettingsContext } from '../../../context/UserSettingsContext';
// @ts-ignore - JavaScript module without types
import { saveUiPreferences } from '../../../utils/api';

export type GridDensity = 'compact' | 'comfortable';
export const DEFAULT_GRID_DENSITY: GridDensity = 'compact';
const STORAGE_KEY = 'forecastDensity';

export function normalizeDensity(value: unknown): GridDensity {
  return value === 'comfortable' ? 'comfortable' : DEFAULT_GRID_DENSITY;
}

function readStored(): GridDensity {
  try {
    return normalizeDensity(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    return DEFAULT_GRID_DENSITY;
  }
}

let current: GridDensity = typeof window === 'undefined' ? DEFAULT_GRID_DENSITY : readStored();
const listeners = new Set<() => void>();

function setCurrent(next: GridDensity) {
  if (next === current) return;
  current = next;
  try { window.localStorage.setItem(STORAGE_KEY, next); } catch { /* private mode */ }
  listeners.forEach((l) => l());
}

/** Back to the default density and forget the stored choice (Classic switch). */
export function resetGridDensity() {
  try { window.localStorage.removeItem(STORAGE_KEY); } catch { /* private mode */ }
  if (current === DEFAULT_GRID_DENSITY) return;
  current = DEFAULT_GRID_DENSITY;
  listeners.forEach((l) => l());
}

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => { listeners.delete(l); };
};

interface SettingsCtx {
  userSettings?: { email?: string; uiPreferences?: Record<string, unknown> } | null;
  updateSettings?: (patch: Record<string, unknown>) => Promise<unknown>;
}

export function useGridDensity(): [GridDensity, (next: GridDensity) => void] {
  const density = useSyncExternalStore(subscribe, () => current, () => DEFAULT_GRID_DENSITY);
  const { userSettings, updateSettings } = (useUserSettingsContext() || {}) as SettingsCtx;
  const serverValue = userSettings?.uiPreferences?.forecastDensity;

  // A saved choice from another device wins over the local default.
  useEffect(() => {
    if (serverValue) setCurrent(normalizeDensity(serverValue));
  }, [serverValue]);

  const setDensity = useCallback((next: GridDensity) => {
    setCurrent(next);
    const email = userSettings?.email;
    if (!email) return;
    const uiPreferences = { ...(userSettings?.uiPreferences || {}), forecastDensity: next };
    Promise.resolve(saveUiPreferences(email, { forecastDensity: next }))
      .then(() => updateSettings?.({ uiPreferences }))
      .catch((e: unknown) => console.error('Error saving grid density:', e));
  }, [userSettings, updateSettings]);

  return [density, setDensity];
}
